import { CHART_TYPE } from "@/app/report/common/constants"

export const MAX_CHART_PER_PAGE = 4
export const DEFAULT_CHART_WIDTH = 1200
export const DEFAULT_CHART_HEIGHT = 400
export const CHART_RATIO = 0.3
// 테이블 최대 row 제한
export const ROW_LEN_LIMIT = 38
export const ROW_LEN_LIMIT_FIRST_PAGE = 27
// 한페이지에 들어갈 수 있는 테이블의 최대 row 수
export const ROW_LEN_ONE_PAGE_LIMIT = 18
// 한페이지에 들어갈 수 있는 테이블의 최대 row 수
export const TABLE_PER_PAGE_BY_ROW = [38, 15, 5, 3]
// 첫번째 페이지에 들어갈 수 있는 테이블의 최대 row 수
export const TABLE_PER_PAGE_BY_ROW_FIRST_PAGE = [20, 7, 4, 3]
export const COLUMN_LEN_LIMIT = 10
/**
 * 한 조각에 넣을 x축 항목 수 — 인덱스는 한 칸에 나란히 그려지는 막대 계열 수.
 *
 * 자르는 쪽(여기)은 설정의 chartType 만 보고 세는데, 그리는 쪽은 flagShow·데이터 유무까지
 * 봐서 값이 전부 0 인 계열은 막대로 세지 않는다. 그래서 실제보다 많이 세는 경우가 있고,
 * 많이 세는 것 자체는 안전하다(우측에 여백이 남을 뿐) — 다만 그만큼 일찍 잘린다.
 *
 * 폭 실측(미리보기 1000px, 축 3개): 항목이 아닌 고정 폭이 271px 이고 남는 689px 을 항목이
 * 나눠 쓴다. 항목당 필요 폭은 막대 1개면 34px, 3개면 56.2px 이므로 수용량은 각각 20개,
 * 12개다. 3번 인덱스를 8 -> 10 으로 올려도 최악(막대 3개)에서 832.8px 로 127px 여유가 있다.
 */
export const MAXIMUM_CHART_LEN_BY_BAR_CHART = [14, 14, 12, 10]

/**
 * 여러 조각으로 나뉜 차트가 공유할 기준 항목 개수 — ChartStyle.referenceXAxisItemCount 에 넣는 값.
 *
 * 상한(MAXIMUM_CHART_LEN_BY_BAR_CHART)을 그대로 기준으로 삼으면 안 된다. 애초에 잘리지도 않은
 * 차트(예: 선수 2명)까지 14개짜리 기준으로 항목 폭을 잡아, 차트 SVG 가 컨테이너보다 훨씬 좁게
 * 그려진다. 그 좁은 SVG 가 컨테이너 폭으로 늘어나면 막대·글자·눈금선이 통째로 확대되고
 * x축 라벨이 잘려나간다.
 *
 * 자르기 전 전체 항목 수로 상한을 씌우면
 *   - 안 잘리는 차트(전체 < 상한): 기준 = 실제 개수 → 종전대로 폭을 다 쓴다
 *   - 잘리는 차트: 기준 = 상한 = 첫 조각의 개수 → 자투리 조각도 같은 막대 폭을 갖는다
 */
export const getReferenceItemCount = (barChartCount: number, totalItemCount: number): number =>
  Math.min(MAXIMUM_CHART_LEN_BY_BAR_CHART[barChartCount] ?? totalItemCount, totalItemCount)

/**
 * 한 x축 칸에 나란히 그려지는 막대 계열 수 — MAXIMUM_CHART_LEN_BY_BAR_CHART 의 인덱스.
 *
 * 스택 막대(stacked)도 막대 폭을 차지한다. 실제로 그리는 쪽(common/chart/multiple.ts)은
 * BAR 와 STACKED_BAR 를 함께 세는데 자르는 쪽은 BAR 만 세고 있어서, 스택만 쓰는 템플릿에서
 * 한 줄에 너무 많은 구간을 배치해 차트 우측이 잘렸다(WEB-2259 와 같은 유형).
 *
 * 그리는 쪽은 같은 단위의 스택끼리 묶어 1개로 세지만 여기서는 단위 정보가 없어 각각 1개로
 * 센다 — 적게 세면 잘리고 많이 세면 오른쪽 여백이 남을 뿐이라 안전한 쪽으로 맞춘다.
 *
 * 이 계수가 여러 파일에 복붙되어 있던 것이 애초에 양쪽이 어긋난 원인이라 한 곳으로 모은다.
 */
export const countBarChartAxes = (yAxis?: Array<{ chartType?: string }>): number =>
  yAxis?.reduce(
    (acc, cur) =>
      cur.chartType === CHART_TYPE.BAR || cur.chartType === CHART_TYPE.STACKED_BAR ? acc + 1 : acc,
    0,
  ) || 0

// ── 미리보기 고정 폭 ────────────────────────────────────────────────
// 출력 폭(mm)은 고정인데 미리보기 폭은 브라우저 창에 비례해서, 창이 넓을수록 같은 내용이
// 더 작게 출력됐다 (2026-08-21 실측, 본문 기준: 창 1268 → 8.19pt / 1920 → 5.06pt /
// 3840(4K 100%) → 2.38pt). 폭을 고정하면 어느 PC 에서 뽑아도 결과물이 같다.
//
// 값이 1000 인 이유: scaleRatio 가 `폭 / 1000` 이라 정확히 1 이 된다. 지금도 다이얼로그가
// 마운트되기 전 첫 렌더에서는 1 이 쓰이므로, 사용자가 처음 보는 미리보기와 동일한 배율이
// 그대로 캡처된다 — 렌더 시점과 캡처 시점의 배율이 어긋나던 문제도 함께 사라진다.
export const PDF_PREVIEW_WIDTH_PX = 1000

// 차트 높이는 `폭 × CHART_RATIO` 로 잡는데 세로 크롬(상·하 여백, 값 표시 공간,
// x축 라벨 높이)은 고정 px 이라, 폭이 줄면 플롯 영역이 먼저 무너진다.
// 실제로 폭 678px(높이 203px)에서 눈금선이 뭉치고 막대가 축 밖으로 나갔다.
// 정상 동작이 확인된 높이는 304px 이므로 그보다 조금 낮은 값을 하한으로 둔다.
export const MIN_CHART_HEIGHT = 280
